import { acceptsFile, describeAccept, formatBytes } from './file-validation';

export type ImageProcess = 'none' | 'fit' | 'cover';

export const INVALID_IMAGE_MESSAGE = 'The selected file is not a valid image. The file extension may not match the actual file content.';

export interface ProcessedImage {
    dataUrl: string;
    width: number;
    height: number;
    sourceWidth: number;
    sourceHeight: number;
    /** The source was smaller than the target box, so the result may look small or blurry. */
    underSized: boolean;
}

export interface ImageRules {
    accept: string | null;
    maxBytes: number | null;
    process: ImageProcess;
    targetWidth: number | null;
    targetHeight: number | null;
    warnIfSmaller: boolean;
}

export interface PreparedImage {
    file: File;
    previewUrl: string;
    /** Processed PNG data URL; null when process is 'none'. */
    dataUrl: string | null;
    underSized: boolean;
}

export function readAsDataUrl(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error(INVALID_IMAGE_MESSAGE));
        reader.readAsDataURL(file);
    });
}

export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(INVALID_IMAGE_MESSAGE));
        img.src = src;
    });
}

async function toImage(source: Blob | string): Promise<HTMLImageElement> {
    return loadImage(typeof source === 'string' ? source : await readAsDataUrl(source));
}

/** Scales down (never up) to fit inside w x h, keeping the aspect ratio; PNG out. */
export async function fitToBox(source: Blob | string, width: number, height: number): Promise<ProcessedImage> {
    const img = await toImage(source);
    const scale = Math.min(1, width / img.width, height / img.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(img.width * scale));
    canvas.height = Math.max(1, Math.floor(img.height * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return {
        dataUrl: canvas.toDataURL('image/png'),
        width: canvas.width,
        height: canvas.height,
        sourceWidth: img.width,
        sourceHeight: img.height,
        underSized: img.width < width && img.height < height,
    };
}

/** Center-crops to the w:h aspect and scales to exactly w x h; PNG out. */
export async function coverCrop(source: Blob | string, width: number, height: number): Promise<ProcessedImage> {
    const img = await toImage(source);
    const ratio = width / height;
    const cropWidth = Math.min(img.width, img.height * ratio);
    const cropHeight = cropWidth / ratio;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(
            img,
            (img.width - cropWidth) / 2,
            (img.height - cropHeight) / 2,
            cropWidth,
            cropHeight,
            0,
            0,
            width,
            height,
    );
    return {
        dataUrl: canvas.toDataURL('image/png'),
        width,
        height,
        sourceWidth: img.width,
        sourceHeight: img.height,
        underSized: cropWidth < width || cropHeight < height,
    };
}

/** Null when the file passes type and size, otherwise the message to show. */
export function validateImage(file: File, accept: string | null | undefined, maxBytes: number | null | undefined): string | null {
    if (!acceptsFile(file, accept)) {
        const formats = describeAccept(accept);
        return formats && formats !== 'image' ? `Choose a ${formats} image.` : 'Choose an image file.';
    }
    if (maxBytes && file.size > maxBytes) {
        return `That image is larger than ${formatBytes(maxBytes)}. Choose a smaller one.`;
    }
    return null;
}

/** Validates, decodes and processes one picked image; rejects with a user-facing message. */
export async function prepareImage(file: File, rules: ImageRules): Promise<PreparedImage> {
    const invalid = validateImage(file, rules.accept, rules.maxBytes);
    if (invalid) {
        throw new Error(invalid);
    }
    const raw = await readAsDataUrl(file);
    const hasBox = !!rules.targetWidth && !!rules.targetHeight;
    if (rules.process !== 'none' && hasBox) {
        const processed = rules.process === 'cover'
            ? await coverCrop(raw, rules.targetWidth, rules.targetHeight)
            : await fitToBox(raw, rules.targetWidth, rules.targetHeight);
        return {
            file,
            previewUrl: processed.dataUrl,
            dataUrl: processed.dataUrl,
            underSized: rules.warnIfSmaller && processed.underSized,
        };
    }
    const img = await loadImage(raw);
    return {
        file,
        previewUrl: raw,
        dataUrl: rules.process === 'none' ? null : raw,
        underSized: rules.warnIfSmaller && hasBox && img.width < rules.targetWidth && img.height < rules.targetHeight,
    };
}

export function underSizedMessage(width: number | null, height: number | null): string {
    return `Uploaded image is under the recommended size (${width} x ${height}px). Saved image may appear smaller or blurry.`;
}
